/**
 * Application-level role determines which app surface a user can enter.
 *
 *  - `user`     → 前台 App (Customers / Partners)
 *  - `operator` → 后台 App, day-to-day ops staff
 *  - `admin`    → 后台 App, full administrative privileges
 *  - `null`     → unauthenticated / not yet resolved
 */
export type AppRole = 'user' | 'operator' | 'admin' | null;

/**
 * Partner role classifies a Partner-tier 前台 user. Customers are special-cased
 * with the `'customer'` value.
 */
export type PartnerRole =
  | 'customer'
  | 'travelAgency'
  | 'eventOrganizer'
  | 'facilityProvider'
  | 'serviceProvider'
  | 'researchPartner';

export const PARTNER_ROLE_VALUES: readonly PartnerRole[] = [
  'customer',
  'travelAgency',
  'eventOrganizer',
  'facilityProvider',
  'serviceProvider',
  'researchPartner',
] as const;

export function isPartnerRole(value: unknown): value is PartnerRole {
  return (
    typeof value === 'string' &&
    (PARTNER_ROLE_VALUES as readonly string[]).includes(value)
  );
}

/** Roles permitted to enter the 后台 (backend) admin app. */
export const BACKEND_APP_ROLES: ReadonlyArray<NonNullable<AppRole>> = [
  'admin',
  'operator',
] as const;

export function canEnterBackendApp(role: AppRole): boolean {
  return role !== null && (BACKEND_APP_ROLES as readonly string[]).includes(role);
}
